import React, { useId } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type PartnrMarkState = 'idle' | 'thinking' | 'done';

export interface PartnrMarkProps {
  /** Rendered size in px, 12 to 32. */
  size?: number;
  state?: PartnrMarkState;
  className?: string;
  /** Accessible name. Omit when the mark sits next to visible text. */
  title?: string;
}

/* Two equal circles overlapping by a third of their diameter: two minds,
   one shared space. Geometry is in a 24-unit box; the stroke does not scale
   with it, so it stays 1.5px from 12 to 32px. */
const R = 6.5;
const CY = 12;
const GAP = (R * 2 * 2) / 3; // center distance: 2r minus a third of the diameter
const LEFT = 12 - GAP / 2;
const RIGHT = 12 + GAP / 2;
// While thinking each circle drifts outward this far and back
const DRIFT = 1.25;
const DRIFT_DUR = '1.6s';
// Ease-in-out for each half of the loop
const SPLINES = '0.45 0 0.55 1; 0.45 0 0.55 1';

function Drift({ from, to }: { from: number; to: number }) {
  return (
    <animate
      attributeName="cx"
      values={`${from};${to};${from}`}
      keyTimes="0;0.5;1"
      calcMode="spline"
      keySplines={SPLINES}
      dur={DRIFT_DUR}
      repeatCount="indefinite"
    />
  );
}

/**
 * The Partnr mark. `thinking` drifts the circles apart and back and fills
 * their overlap with the signal wash; `done` pops once. Under reduced motion
 * thinking is a slow opacity pulse and done is still.
 */
export function PartnrMark({ size = 16, state = 'idle', className, title }: PartnrMarkProps) {
  const still = useReducedMotion();
  // useId returns ":r1:"; colons are not safe inside url(#...)
  const clipId = `partnr-mark-${useId().replace(/:/g, '')}`;
  const px = Math.min(32, Math.max(12, size));
  const thinking = state === 'thinking';
  const drifting = thinking && !still;

  return (
    <motion.svg
      // Re-keyed on done so the pop plays once each time a run finishes
      key={state === 'done' ? 'done' : 'live'}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      data-state={state}
      initial={state === 'done' && !still ? { scale: 0.86 } : false}
      animate={{ scale: 1 }}
      transition={{ duration: 0.32, ease: [0.24, 1.34, 0.38, 1] }}
      className={cn('shrink-0 overflow-visible', thinking && still && 'partnr-mark-pulse', className)}
    >
      {title ? <title>{title}</title> : null}
      {thinking && (
        <>
          <defs>
            <clipPath id={clipId}>
              <circle cx={LEFT} cy={CY} r={R}>
                {drifting && <Drift from={LEFT} to={LEFT - DRIFT} />}
              </circle>
            </clipPath>
          </defs>
          {/* The overlap: the right circle clipped by the left one */}
          <circle cx={RIGHT} cy={CY} r={R} clipPath={`url(#${clipId})`} style={{ fill: 'hsl(var(--signal-wash))' }}>
            {drifting && <Drift from={RIGHT} to={RIGHT + DRIFT} />}
          </circle>
        </>
      )}
      <circle cx={LEFT} cy={CY} r={R} stroke="currentColor" strokeWidth={1.5} vectorEffect="non-scaling-stroke">
        {drifting && <Drift from={LEFT} to={LEFT - DRIFT} />}
      </circle>
      <circle cx={RIGHT} cy={CY} r={R} stroke="currentColor" strokeWidth={1.5} vectorEffect="non-scaling-stroke">
        {drifting && <Drift from={RIGHT} to={RIGHT + DRIFT} />}
      </circle>
    </motion.svg>
  );
}
